import { nextTick, reactive, ref } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { FAVORITE_TYPE } from '@/typings/enums/FAVORITE_TYPE';
import { onClickOutside } from '@vueuse/core';
import { debounce, throttle } from 'lodash-es';
import useUserFavoriteService from '@/services/user/UserFavoriteService';
import { useWindow } from '@orion.ui/orion';

type Props = SetupProps<typeof SharedMainNavSetupService.props>

export default class SharedMainNavSetupService extends BaseSetupService<Props> {
	static readonly props = {
		unreadNewsCount: {
			type: Number,
			default: 0,
		},
	};

	readonly _navMain = ref<OrionNavMain[]>();
	freshWorksWidget: any;

	private readonly wrapperId = 'mobile-nav-children-clone-wrapper';
	private readonly state = reactive({
		favorites: [] as IUserFavoriteModel[],
		touchTimer: 0,
		isScolling: false,
		currentOpenedMenuIndex: undefined as Undef<number>,
	});

	private get mobileNavChildrenCloneWrapper () {
		return document.getElementById(this.wrapperId);
	}

	private get userTypeNavigation () {
		if (this.self.isAgency()) {
			return this.agencyNavigation;
		} else if (this.self.isClient()) {
			return this.clientNavigation;
		} else if (this.self.isTempWorker()) {
			return this.tempWorkerNavigation;
		}
		return [];
	}

	private get agencyNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: 'Clients',
				fontIcon: 'icon-shopping-building',
				if: this.self.canOneOf(
					'ClientUserRead',
					'ClientContractRead',
					'BillRead',
					'RegisterRead',
					'HandicappedWorkerListRead',
					'ClientProvisionDocumentRead',
					'TimeTicketRead',
				),
				children: [
					{
						label: `Liste des utilisateurs clients`,
						favoritetype: FAVORITE_TYPE.MesClientsClients,
						to: {
							name: 'User',
							//params: { type: 'clients' },
						},
						if: this.self.can('ClientUserRead'),
					},
					{
						label: `Documents`,
						line: true,
						if: this.self.canOneOf(
							'ClientContractRead',
							'BillRead',
							'RegisterRead',
							'HandicappedWorkerListRead',
							'ClientProvisionDocumentRead',
						),
					},
					{
						label: `Contrats`,
						favoritetype: FAVORITE_TYPE.MesClientsContrats,
						to: { name: 'ClientContract' },
						if: this.self.can('ClientContractRead'),
					},
					{
						label: `Factures`,
						favoritetype: FAVORITE_TYPE.MesClientsFactures,
						to: { name: 'Bill' },
						if: this.self.can('BillRead'),
					},
					{
						label: `Registres du personnel`,
						favoritetype: FAVORITE_TYPE.MesClientsRegistresDuPersonnel,
						to: { name: 'Register' },
						if: this.self.can('RegisterRead'),
					},
					{
						label: `Liste des travailleurs handicapés`,
						favoritetype: FAVORITE_TYPE.MesClientsListeTravailleursHandicapes,
						to: { name: 'HandicappedWorkerList' },
						if: this.self.can('HandicappedWorkerListRead'),
					},
					{
						label: `Documents mis à disposition`,
						favoritetype: FAVORITE_TYPE.MesClientsDocumentsMisADisposition,
						to: { name: 'ClientProvisionDocument' },
						if: this.self.can('ClientProvisionDocumentRead'),
					},
					{
						label: `Traîtements`,
						line: true,
						if: this.self.canOneOf('TimeTicketRead', 'ClientContractRead'),
					},
					{
						label: `Relevés d'heures`,
						favoritetype: FAVORITE_TYPE.MesClientsRelevesHeures,
						to: {
							name: 'TimeTicketYear',
							params: { year: new Date().getFullYear().toString() },
						},
						if: this.self.can('TimeTicketRead'),
					},
					{
						label: `Prolongations de contrats`,
						favoritetype: FAVORITE_TYPE.MesClientsProlongationsContrats,
						to: { name: 'ContractProlongation' },
						if: this.self.can('ClientContractRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Intérimaires',
				fontIcon: 'icon-interface-user-multiple',
				if: this.self.canOneOf(
					'TempWorkerUserRead',
					'TempWorkerContractRead',
					'SalarySlipRead',
					'CertificateRead',
					'WithholdingTaxAttestationRead',
					'TripAttestationRead',
					'PoleEmploiAttestationRead',
					'TempWorkerProvisionDocumentRead',
				),
				children: [
					{
						label: `Liste des intérimaires`,
						favoritetype: FAVORITE_TYPE.MesInterimairesInterimaires,
						to: { name: 'TempWorker' },
						if: this.self.can('TempWorkerUserRead'),
					},
					{
						label: `Documents`,
						line: true,
						if: this.self.canOneOf(
							'TempWorkerContractRead',
							'SalarySlipRead',
							'CertificateRead',
							'WithholdingTaxAttestationRead',
							'TripAttestationRead',
							'PoleEmploiAttestationRead',
							'TempWorkerProvisionDocumentRead',
						),
					},
					{
						label: `Contrats`,
						favoritetype: FAVORITE_TYPE.MesInterimairesContrats,
						to: { name: 'TempWorkerContract' },
						if: this.self.can('TempWorkerContractRead'),
					},
					{
						label: `Bulletins de salaire`,
						favoritetype: FAVORITE_TYPE.MesInterimairesBulletinsSalaire,
						to: { name: 'SalarySlip' },
						if: this.self.can('SalarySlipRead'),
					},
					{
						label: `Certificats de travail`,
						favoritetype: FAVORITE_TYPE.MesInterimairesCertificatsTravail,
						to: { name: 'Certificate' },
						if: this.self.can('CertificateRead'),
					},
					{
						label: `Attestations d'impôts`,
						favoritetype: FAVORITE_TYPE.MesInterimairesAttestationsImpots,
						to: { name: 'WithholdingTaxAttestation' },
						if: this.self.can('WithholdingTaxAttestationRead'),
					},
					{
						label: `Justificatifs de déplacements`,
						favoritetype: FAVORITE_TYPE.MesInterimairesJustificatifsDeplacements,
						to: { name: 'TripAttestation' },
						if: this.self.can('TripAttestationRead'),
					},
					{
						label: `Attestations employeur France Travail`,
						favoritetype: FAVORITE_TYPE.MesInterimairesAttestationsPoleEmploi,
						to: { name: 'PoleEmploiAttestation' },
						if: this.self.can('PoleEmploiAttestationRead'),
					},
					{
						label: `Documents mis à disposition`,
						favoritetype: FAVORITE_TYPE.MesInterimairesDocumentsMisADisposition,
						to: { name: 'TempWorkerProvisionDocument' },
						if: this.self.can('TempWorkerProvisionDocumentRead'),
					},
					{
						label: `Traîtements`,
						line: true,
						if: this.self.can('AdvancedPaymentDemandRead'),
					},
					{
						label: `Demandes d'acompte`,
						favoritetype: FAVORITE_TYPE.MesInterimairesDemandesAcompte,
						to: { name: 'AdvancedPaymentDemand' },
						if: this.self.can('AdvancedPaymentDemandRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Commandes',
				fontIcon: 'icon-shopping-cart-1',
				if: this.self.canOneOf('OrderRead', 'OrderToolbox') && this.self.can('TempWorkerUserRead'),
				children: [
					{
						label: `Vue d'ensemble`,
						favoritetype: FAVORITE_TYPE.CommandesVueDensemble,
						to: { name: 'OrderDashboard' },
						if: this.self.can('OrderRead') && this.self.can('TempWorkerUserRead'),
					},
					{
						label: `Commandes`,
						favoritetype: FAVORITE_TYPE.CommandesCommandes,
						to: { name: 'Order' },
						if: this.self.can('OrderRead') && this.self.can('TempWorkerUserRead'),
					},
					{
						label: `Boîte à outils`,
						favoritetype: FAVORITE_TYPE.CommandesBoiteAOutils,
						to: { name: 'ToolboxTalentPool' },
						if: this.self.can('OrderToolbox') && this.self.can('TempWorkerUserRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Organisation',
				fontIcon: 'icon-interface-hierarchy-2',
				if: this.self.canOneOf(
					'OrganizationRead',
					'UserRead',
					'OrganizationRoleRead',
					'TagRead',
				),
				children: [
					{
						label: `Vision d'ensemble`,
						favoritetype: FAVORITE_TYPE.OrganisationVisionDensemble,
						to: { name: 'OrganizationDashboard' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: `Sociétés`,
						favoritetype: FAVORITE_TYPE.OrganisationSocietes,
						to: { name: 'OrganizationCompany' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: `Utilisateurs`,
						favoritetype: FAVORITE_TYPE.OrganisationMesUtilisateurs,
						to: { name: 'OrganizationUser' },
						if: this.self.can('UserRead'),
					},
					{
						label: `Rôles`,
						favoritetype: FAVORITE_TYPE.OrganisationRoles,
						to: { name: 'OrganizationRoles' },
						if: this.self.can('OrganizationRoleRead'),
					},
					{
						label: `Tags`,
						favoritetype: FAVORITE_TYPE.OrganisationTags,
						to: { name: 'OrganizationTags' },
						if: this.self.can('TagRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Paramètres',
				fontIcon: 'icon-interface-setting-cog',
				if: this.self.canOneOf(
					'CompanyDigitalSafeDocumentRead',
					'AgencyNewsRead',
					'Statistics',
					'ConditionRead',
					'CompanySettingUpdate',
				),
				children: [
					{
						label: `Archives`,
						favoritetype: FAVORITE_TYPE.ParametresArchives,
						id: 'archives',
						to: { name: 'DigitalSafe' },
						if: this.self.can('CompanyDigitalSafeDocumentRead'),
					},
					{
						label: `Actualités`,
						favoritetype: FAVORITE_TYPE.ParametresActualites,
						to: { name: 'AgencyNews' },
						if: this.self.can('AgencyNewsRead'),
					},
					{
						label: `Statistiques`,
						favoritetype: FAVORITE_TYPE.ParametresStatistiques,
						to: { name: 'Statistic' },
						if: this.self.can('Statistics'),
					},
					{
						label: `CGV & CTT`,
						favoritetype: FAVORITE_TYPE.ParametresCgvCtt,
						to: { name: 'CgvCttBase' },
						if: this.self.can('ConditionRead'),
					},
					{
						label: `Paramètres`,
						favoritetype: FAVORITE_TYPE.ParametresParametres,
						to: { name: 'SettingsReminders' },
						if: this.self.can('CompanySettingUpdate'),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Guides',
				fontIcon: 'icon-computer-logo-safari',
				children: [
					{
						label: `Guide utilisateur agence`,
						callback: () => window.open(
							`https://enso-groupe.notion.site/ARMADO_Guide-utilisateurs_Agence_v6-0-0-fa28ecfc8f2f46bba73efa66979b92f8`,
							'_blank',
						),
					},
					{
						label: `Guide utilisateur client`,
						callback: () => window.open(
							`https://enso-groupe.notion.site/ARMADO_Guide-utilisateurs_Client_v6-0-0-976e4e10a35d455abf00b58d79ff07ba`,
							'_blank',
						),
					},
					{
						label: `Guide utilisateur intérimaire`,
						callback: () => window.open(
							`https://enso-groupe.notion.site/ARMADO_Guide-utilisateurs_Interimaire_v6-0-0-54809982fee14a47a8313e5629048f2e`,
							'_blank',
						),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Aide support',
				fontIcon: 'icon-interface-help-question-circle',
				callback: () => {this.freshWorksWidget('open');},
			},
		];

		return nav;
	}

	private get clientNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: 'Documents',
				fontIcon: 'icon-interface-file-folder',
				if: this.self.canOneOf(
					'ClientContractRead',
					'BillRead',
					'RegisterRead',
					'HandicappedWorkerListRead',
					'ClientProvisionDocumentRead',
				),
				children: [
					{
						label: `Contrats`,
						favoritetype: FAVORITE_TYPE.MesDocumentsContrats,
						to: { name: 'ClientContract' },
						if: this.self.can('ClientContractRead'),
					},
					{
						label: `Factures`,
						favoritetype: FAVORITE_TYPE.MesDocumentsFactures,
						to: { name: 'Bill' },
						if: this.self.can('BillRead'),
					},
					{
						label: `Registres du personnel`,
						favoritetype: FAVORITE_TYPE.MesDocumentsRegistresDuPersonnel,
						to: { name: 'Register' },
						if: this.self.can('RegisterRead'),
					},
					{
						label: `Liste des travailleurs handicapés`,
						favoritetype: FAVORITE_TYPE.MesDocumentsListeTravailleursHandicapes,
						to: { name: 'HandicappedWorkerList' },
						if: this.self.can('HandicappedWorkerListRead'),
					},
					{
						label: `Documents mis à disposition`,
						favoritetype: FAVORITE_TYPE.MesDocumentsDocumentsMisADisposition,
						to: { name: 'ClientProvisionDocument' },
						if: this.self.can('ClientProvisionDocumentRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Traitements',
				fontIcon: 'icon-interface-edit-user-experience-design',
				if: this.self.canOneOf('TimeTicketRead', 'ClientContractRead', 'OrderRead'),
				children: [
					{
						label: `Relevés d'heures`,
						favoritetype: FAVORITE_TYPE.MesTraitementsRelevesHeures,
						to: {
							name: 'TimeTicketYear',
							params: { year: new Date().getFullYear().toString() },
						},
						if: this.self.can('TimeTicketRead'),
					},
					{
						label: `Prolongations de contrats`,
						favoritetype: FAVORITE_TYPE.MesTraitementsProlongationsContrats,
						to: { name: 'ContractProlongation' },
						if: this.self.can('ClientContractRead'),
					},
					{
						label: `Commandes`,
						favoritetype: FAVORITE_TYPE.MesTraitementsCommandes,
						to: { name: 'Order' },
						if: this.self.can('OrderRead'),
					},
				] as ArmNavItem[],
			},
			{
				label: 'Organisation',
				fontIcon: 'icon-interface-hierarchy-2',
				if: this.self.canOneOf(
					'OrganizationRead',
					'UserRead',
					'OrganizationRoleRead',
					'TagRead',
					'OrganizationUpdate',
					'MissionPlace',
				),
				children: [
					{
						label: `Vision d'ensemble`,
						favoritetype: FAVORITE_TYPE.OrganisationVisionDensemble,
						to: { name: 'OrganizationDashboard' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: `Sociétés`,
						favoritetype: FAVORITE_TYPE.OrganisationSocietes,
						to: { name: 'OrganizationCompany' },
						if: this.self.can('OrganizationRead'),
					},
					{
						label: `Utilisateurs`,
						favoritetype: FAVORITE_TYPE.OrganisationMesUtilisateurs,
						to: { name: 'OrganizationUser' },
						if: this.self.can('UserRead'),
					},
					{
						label: `Rôles`,
						favoritetype: FAVORITE_TYPE.OrganisationRoles,
						to: { name: 'OrganizationRoles' },
						if: this.self.can('OrganizationRoleRead'),
					},
					{
						label: `Tags`,
						favoritetype: FAVORITE_TYPE.OrganisationTags,
						to: { name: 'OrganizationTags' },
						if: this.self.can('TagRead'),
					},
					{
						label: `Paramètres`,
						favoritetype: FAVORITE_TYPE.OrganisationParametres,
						to: { name: 'Settings' },
						if: this.self.can('OrganizationUpdate'),
					},
					{
						label: `Lieux de mission`,
						favoritetype: FAVORITE_TYPE.OrganisationLieuxDeMission,
						to: { name: 'MissionPlace' },
						if: this.self.can('MissionPlace'),
					},
				] as ArmNavItem[],
			},
			{
				label: `Actualités`,
				fontIcon: 'icon-interface-content-file',
				to: { name: 'News' },
			},
			{
				label: 'Aide en ligne',
				fontIcon: 'icon-computer-logo-safari',
				callback: () => window.open(
					`https://enso-groupe.notion.site/ARMADO_Guide-utilisateurs_Client_v6-0-0-976e4e10a35d455abf00b58d79ff07ba`,
					'_blank',
				),
			},
		];

		return nav;
	}

	private get tempWorkerNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: 'Mes documents',
				line: true,
			},
			{
				label: 'Vie personnelle',
				fontIcon: 'icon-interface-user-circle',
				to: { name: 'DigitalSafe' },
			},
			{
				label: this.ui.onLayoutNavCollapsed && !this.responsive.onPhone ? 'Vie pro.' : 'Vie professionnelle',
				fontIcon: 'icon-interface-setting-tool-box',
				children: [
					{
						label: `Contrats`,
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleContrats,
						to: { name: 'TempWorkerContract' },
					},
					{
						label: `Bulletins de salaire`,
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleBulletinsSalaire,
						to: { name: 'SalarySlip' },
					},
					{
						label: `Certificats de travail`,
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleCertificatsTravail,
						to: { name: 'Certificate' },
					},
					{
						label: `Attestations d'impôts`,
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleAttestionsImpots,
						to: { name: 'WithholdingTaxAttestation' },
					},
					{
						label: `Justificatifs de déplacement`,
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleJustificatifsDeplacements,
						to: { name: 'TripAttestation' },
					},
					{
						label: `Attestations Employeur France Travail`,
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleAttestationsPoleEmploi,
						to: { name: 'PoleEmploiAttestation' },
					},
					{
						label: `Documents mis à disposition`,
						favoritetype: FAVORITE_TYPE.MesDocumentsVieProfessionnelleDocumentsMisADisposition,
						to: { name: 'TempWorkerProvisionDocument' },
					},
				] as ArmNavItem[],
			},
			{
				label: 'Mes traitements',
				line: true,
			},
			{
				label: `Disponibilités`,
				fontIcon: 'icon-interface-calendar-mark',
				favoritetype: FAVORITE_TYPE.MesTraitementsDisponibilites,
				to: { name: 'UserProfileAvailabilities' },
			},
			{
				label: `Demandes d'acompte`,
				fontIcon: 'icon-money-cash-file-dollar',
				favoritetype: FAVORITE_TYPE.MesTraitementsDemandesAcompte,
				to: { name: 'AdvancedPaymentDemand' },
			},
			{
				label: `Relevés d'heures`,
				fontIcon: 'icon-interface-time-book-2',
				favoritetype: FAVORITE_TYPE.MesTraitementsRelevesHeuresInterimaire,
				to: { name: 'TimeTicket' },
			},
			{
				label: `Offres de mission`,
				fontIcon: 'icon-shopping-business-handshake',
				favoritetype: FAVORITE_TYPE.MesTraitementsOffresDeMission,
				to: { name: 'JobOffer' },
			},
			{
				label: `Mes agences`,
				fontIcon: 'icon-shopping-store-phone',
				favoritetype: FAVORITE_TYPE.MesTraitementsMesAgences,
				to: { name: 'UserProfileCompanies' },
			},
			{ line: true },
			{
				label: `Actualités`,
				fontIcon: 'icon-interface-content-file',
				to: { name: 'News' },
			},
			{
				label: `Aide en ligne`,
				fontIcon: 'icon-computer-logo-safari',
				callback: () => window.open(
					`https://enso-groupe.notion.site/ARMADO_Guide-utilisateurs_Interimaire_v6-0-0-54809982fee14a47a8313e5629048f2e`,
					'_blank',
				),
			},
		];

		return nav;
	}

	private get flatSluggedMenuItems (): ArmNavItem[] {
		return this.userTypeNavigation.flatMap((x) => {
			if (x.children?.length) {
				return x.children.map(y => ({
					...y,
					slug: this.menuItemSlug({
						...y,
						parent: x,
					}),
				}));
			}

			return {
				...x,
				slug: this.menuItemSlug(x),
			};
		});
	}

	private get commonNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: `Tableau de bord`,
				fontIcon: 'icon-interface-dashboard-layout-3',
				to: { name: 'Dashboard' },
				root: true,
				wrapperClass: 'arm-nav-menu-common',
			},
		];

		return nav;
	}

	private get favoriteNavigation () {
		const fav = this.flatSluggedMenuItems.filter(x => this.itemIsFavorite(x));

		const nav: ArmNavItem[] = [];

		if (fav.length) {
			nav.push(...[
				{
					label: `Favoris`,
					sectionTitle: true,
					wrapperClass: 'arm-nav-menu-favorite',
				},
				...fav.map(x => ({
					...x,
					wrapperClass: 'arm-nav-menu-favorite',
					processing: false,
				})),
			] as ArmNavItem[]);
		}

		return nav;
	}

	private get mainNavigation () {
		const nav: ArmNavItem[] = [
			{
				label: `Menu`,
				sectionTitle: true,
				wrapperClass: 'arm-nav-menu-base-section-title',
				if: () => !!this.favoriteNavigation.length && !this.self.isTempWorker(),
			},
			...this.userTypeNavigation.map(x => ({
				...x,
				wrapperClass: x.line ? 'arm-nav-menu-classic arm-nav-menu-classic--line' : 'arm-nav-menu-classic',
				processing: false,
				children: x.children?.map(y => ({
					...y,
					processing: false,
				})),
			})),
		];

		return nav;
	}

	get navigation (): Record<string, ArmNavItem[]> {
		if ((this.ui.onLayoutNavBottom || this.ui.onLayoutNavCollapsed) && !this.responsive.onPhone) {
			return {
				main: [
					...this.commonNavigation,
					...this.mainNavigation,
				],
			};
		} else {
			return {
				common: this.commonNavigation,
				favorites: this.favoriteNavigation,
				main: this.mainNavigation,
			};
		}
	}

	windowResizeHandler = debounce(async () => {
		this.addTouchListeners();
	}, 17);


	constructor (props?: Props) {
		super(props);
		this.iniNavAsync();

		window.addEventListener('resize', this.debouncedWindowResizeHandler.bind(this));
		this.freshWorksWidget = useWindow()?.FreshworksWidget;
		if (this.freshWorksWidget) {
			this.freshWorksWidget('identify', 'ticketForm', {
				'name': this.self?.name,
				'email': this.self?.authLogin,
			});
		}
	}

	protected onBeforeUnmount () {
		window?.removeEventListener('resize', this.debouncedWindowResizeHandler.bind(this));
	}

	private debouncedWindowResizeHandler () {
		this.windowResizeHandler();
	}

	private async iniNavAsync () {
		// Get favorites
		this.state.favorites = await useUserFavoriteService().listAsync();

		nextTick(() => {
			this.addTouchListeners();
		});
	}

	private menuItemSlug (item: ArmNavSlugedItem) {
		const itemSlugArray = [];
		if (item.parent?.label) itemSlugArray.push(item.parent.label);
		itemSlugArray.push(item.label);

		return itemSlugArray.join('/');
	}

	itemIsFavorite (item: ArmNavSlugedItem) {
		return item.favoritetype !== undefined && this.state.favorites.mapKey('type').includes(item.favoritetype);
	}

	async toggleMenuItemFavoriteAsync (item: ArmNavSlugedItem) {
		if (item.favoritetype !== undefined && this.itemIsFavorite(item)) {
			const fav = this.state.favorites.findByKey(item.favoritetype, 'type');
			if (fav) {
				item.processing = true;
				await useUserFavoriteService().deleteAsync(fav.id);
				item.processing = false;
				this.state.favorites.deleteWhere('id', fav.id);
			}
		} else if (item.favoritetype !== undefined) {
			item.processing = true;
			const data = await useUserFavoriteService().addAsync({ type: item.favoritetype });
			item.processing = false;
			this.state.favorites.push(data);
		}
	}

	private addTouchListeners () {
		const el = this._navMain.value?.first()?._el();

		if (!!el) {
			el.addEventListener('mouseover', throttle(e => this.setFloatingUiAsync(e.target as HTMLElement), 100));
			el.addEventListener('click', e => this.setFloatingUiAsync(e.target as HTMLElement));
		}
	}

	private appendMobileNavChildrenCloneWrapper (dataIndex: number, children: HTMLElement) {
		const childrenCloneWrapper = document.createElement('div');
		childrenCloneWrapper.id = this.wrapperId;
		childrenCloneWrapper.classList.add('arm-nav', 'orion-nav-main');
		childrenCloneWrapper.setAttribute('data-index', dataIndex.toString());

		childrenCloneWrapper.append(children);

		document.body.append(childrenCloneWrapper);

		setTimeout(() => {
			if (this._navMain.value?.first()) {
				onClickOutside(this._navMain.value.first()?._el(), () => this.removeMobileNavChildrenCloneWrapper());
			}
		}, 10);
	}

	private removeMobileNavChildrenCloneWrapper () {
		document.querySelectorAll('.arm-nav-menu-classic--active').forEach(el => el.classList.remove('arm-nav-menu-classic--active'));

		this.state.currentOpenedMenuIndex = undefined;

		if (!this.mobileNavChildrenCloneWrapper) return;

		const targetMenuItemIndex = this.mobileNavChildrenCloneWrapper.getAttribute('data-index');

		if (targetMenuItemIndex) {
			const submenu = this.mobileNavChildrenCloneWrapper.children.item(0);
			const targetMenuItem = this._navMain.value
				?.first()?._el()
				?.querySelector(`.orion-nav-main__wrapper > .orion-nav-main__children > [data-index="${targetMenuItemIndex}"]`);

			if (targetMenuItem && submenu) {
				submenu.removeAttribute('style');
				targetMenuItem.append(submenu);
			}
		}

		setTimeout(() => {
			this.mobileNavChildrenCloneWrapper?.remove();
		}, 50);
	}

	private async setFloatingUiAsync (target: HTMLElement) {
		if (this.ui.onLayoutNavFull || this.responsive.onPhone) return;

		if (target.classList.contains('arm-nav-menu__item')) {
			target = target.parentElement as HTMLElement;
		}

		let parent = target.parentElement;

		if (parent?.classList.contains('orion-nav-main__item')) {
			parent = parent.parentElement;
		}

		const dataIndex = +(parent?.getAttribute('data-index') ?? 0);

		if (!!dataIndex && this.state.currentOpenedMenuIndex === dataIndex) {
			// stop here if it's the same submenu
			return;
		}

		this.removeMobileNavChildrenCloneWrapper();

		const children = target.nextElementSibling as HTMLElement;

		if (!!children && !!dataIndex) {
			nextTick(async () => {
				if (target.classList.contains('orion-nav-main__item') && parent?.classList.contains('arm-nav-menu-classic')) {
					parent.classList.add('arm-nav-menu-classic--active');

					this.state.currentOpenedMenuIndex = dataIndex;

					Object.assign(children.style, { opacity: '0' });

					this.appendMobileNavChildrenCloneWrapper(dataIndex, children);

					const { x, y } = await computePosition(target, children, {
						strategy: 'fixed',
						placement: this.ui.onLayoutNavCollapsed ? 'right-start' : 'top',
						middleware: [
							offset({ alignmentAxis: this.ui.onLayoutNavCollapsed ? -5 : 0 }),
							flip(),
							shift({ padding: 16 }),
						],
					});

					Object.assign(children.style, {
						left: `${x}px`,
						top: `${y}px`,
						opacity: '1',
					});

				}
			});
		}
	}
}
